import * as THREE from "three";
import Experience from "../Experience.js";
import TexturedReflector from "../Utils/TexturedReflector.js";

export default class Room {
    constructor() {
        this.experience = new Experience();
        this.debug = this.experience.debug;
        this.scene = this.experience.scene;
        this.renderer = this.experience.renderer.instance;
        this.resources = this.experience.resources;

        this.textures = new Map();
        this.textures.set("roomTexture", this.resources.items.roomTexture);
        this.textures.set("room2Texture", this.resources.items.room2Texture);
        this.textures.set("floorTexture", this.resources.items.floorTexture);
        this.textures.set(
            "blackShinyTexture",
            this.resources.items.blackShinyTexture,
        );
        this.textures.set("bulbTexture", this.resources.items.bulbTexture);
        this.textures.set("goldTexture", this.resources.items.goldTexture);
        this.textures.set(
            "blackMatteTexture",
            this.resources.items.blackMatteTexture,
        );
        this.textures.set(
            "brownMatteTexture",
            this.resources.items.brownMatteTexture,
        );
        this.textures.set("silverTexture", this.resources.items.silverTexture);
        this.textures.set(
            "silverMatteTexture",
            this.resources.items.silverMatteTexture,
        );
        this.textures.set(
            "counterTexture",
            this.resources.items.counterTexture,
        );

        // loop set up all textures
        this.textures.forEach((texture, key) => {
            texture.colorSpace = THREE.SRGBColorSpace;
            texture.flipY = false;
            // leave generateMipmaps = true, for default performance win
            // set anisotropy to higher, such that the texture is more sharp and less blurred when viewed in grazing angles or in the distance
            texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
            // See how filtering works at: https://threejs.org/manual/#en/textures#filtering-and-mips
            // minFilter's default value is already LinearMipmapLinearFilter
            // meaning choose 2 mips, choose 4 pixels from each and blend all 8 into 1 pixel
            texture.minFilter = THREE.LinearMipMapLinearFilter;
            // magFilter's default value is already LinearFilter
            // meaning blending the 4 closest pixels
            texture.magFilter = THREE.LinearFilter;
            // wrapping
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
        });

        this.setRoom();
        this.setDebugUI();
    }

    setRoom() {
        this.room = this.resources.items.roomModel.scene;
        this.room.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                // console.log(child);
                if (child.name === "Room1") {
                    child.material = new THREE.MeshBasicMaterial({
                        map: this.textures.get("roomTexture"),
                    });
                }
                if (child.name === "Room2") {
                    child.material = new THREE.MeshBasicMaterial({
                        map: this.textures.get("room2Texture"),
                    });
                }

                if (child.name === "FixedWindow") {
                    child.material = new THREE.MeshMatcapMaterial({
                        matcap: this.textures.get("blackShinyTexture"),
                    });
                }

                if (child.name === "GlassPanels") {
                    child.material = new THREE.MeshStandardMaterial({
                        color: new THREE.Color(0xffffff),
                        metalness: 0.8,
                        roughness: 0.2,
                        transparent: true,
                        opacity: 0.5,
                    });
                }

                if (child.name === "CounterTop") {
                    child.material = new THREE.MeshStandardMaterial({
                        color: new THREE.Color(0xffffff),
                        metalness: 0.8,
                        roughness: 0.2,
                        // envMap: this.resources.items.environmentMapTexture, // need explicitly defined if you want separate intensity from global envmap
                        // envMapIntensity: 0.28,
                    });
                }

                if (child.name === "SilverShiny") {
                    child.material = new THREE.MeshMatcapMaterial({
                        matcap: this.textures.get("silverTexture"),
                    });
                }
                if (child.name === "SilverMatte") {
                    child.material = new THREE.MeshMatcapMaterial({
                        matcap: this.textures.get("silverMatteTexture"),
                    });
                }

                if (child.name === "Bulbs") {
                    child.material = new THREE.MeshMatcapMaterial({
                        matcap: this.textures.get("bulbTexture"),
                    });
                }
                if (child.name === "CeilingLampCase") {
                    child.material = new THREE.MeshMatcapMaterial({
                        matcap: this.textures.get("goldTexture"),
                    });
                }
                if (child.name === "SpotLampFrame") {
                    child.material = new THREE.MeshMatcapMaterial({
                        matcap: this.textures.get("silverTexture"),
                    });
                }
                if (child.name === "BlackMatte") {
                    child.material = new THREE.MeshMatcapMaterial({
                        matcap: this.textures.get("blackMatteTexture"),
                    });
                }
                if (child.name === "MiamiChairs") {
                    child.material = new THREE.MeshMatcapMaterial({
                        matcap: this.textures.get("brownMatteTexture"),
                    });
                }

                if (child.name === "GrnFloor") {
                    // Reflector expects geometry to be in the XY plane (local Z is normal)
                    // GLTF floors are often in the XZ plane. We rotate the geometry to XY
                    // and then rotate the mesh so it lies flat again.
                    const geometry = child.geometry.clone();
                    geometry.rotateX(Math.PI / 2);

                    this.reflector = new TexturedReflector(geometry, {
                        map: this.textures.get("floorTexture"),
                        color: new THREE.Color(0xffffff),
                        reflectivity: 0.15, // Adjust this value (0.0 to 1.0)
                        exposure: 2.0, // Adjust this value for tonemapping
                        textureWidth:
                            window.innerWidth * window.devicePixelRatio,
                        textureHeight:
                            window.innerHeight * window.devicePixelRatio,
                    });

                    // Match original transformations
                    this.reflector.position.copy(child.position);
                    this.reflector.rotation.copy(child.rotation);
                    this.reflector.scale.copy(child.scale);

                    // Rotate the mesh to lay it back on the floor (since we rotated geometry)
                    this.reflector.rotateX(-Math.PI / 2);

                    this.room.add(this.reflector);
                    child.visible = false; // Hide instead of remove to preserve hierarchy if needed
                }
            }
        });
        this.room.rotation.y = -Math.PI / 2;
        this.scene.add(this.room);
    }

    setDebugUI() {
        if (this.debug.active) {
            this.debugFolder = this.debug.ui.addFolder("Room");
        }
        // Add to debug UI
        if (this.debug.active) {
            this.debugFolder
                .add(this.reflector, "reflectivity")
                .min(0)
                .max(1)
                .step(0.001)
                .name("Floor Reflectivity");
            this.debugFolder
                .add(this.reflector, "exposure")
                .min(0)
                .max(5)
                .step(0.1)
                .name("Floor Exposure");
        }
    }
}
